// PROMPT 150: bloquear y desbloquear a un cliente (el bloqueo lo hace Meta) y el cartel "Bloqueado".
import { useCallback, useState } from "react";
import { Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BotonNota } from "@/components/agente/NotaEquipo";
import { nombreVisible } from "@/lib/marcasWa";

export const AVISO_BLOQUEO = "No va a poder escribirte por este número. Lo podés desbloquear cuando quieras.";

export function ChipBloqueado({ className = "" }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 ${className}`}
      title="Este cliente está bloqueado: no puede escribir por este número."
      data-testid="wa-chip-bloqueado"
    >
      <Ban className="h-3 w-3" />
      Bloqueado
    </span>
  );
}

// Se usa una sola vez por pantalla: devuelve el estado, las dos acciones y el dialogo de confirmacion
// (hay que dibujarlo una vez). onCambio(id, { bloqueado }) avisa a la pantalla que cambio.
export function useBloqueo(conversacion, onCambio) {
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const id = conversacion ? conversacion.id : null;
  const bloqueado = Boolean(conversacion && conversacion.bloqueado);
  const nombre = conversacion ? nombreVisible(conversacion) : "";

  const cambiar = useCallback(
    async (bloquear) => {
      if (!id) return;
      setOcupado(true);
      try {
        const accion = bloquear ? "bloquear" : "desbloquear";
        await api.post(`/whatsapp/conversaciones/${encodeURIComponent(id)}/${accion}`);
        toast.success(bloquear ? "Cliente bloqueado." : "Cliente desbloqueado.");
        if (onCambio) onCambio(id, { bloqueado: bloquear });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setOcupado(false);
        setConfirmando(false);
      }
    },
    [id, onCambio]
  );

  const dialogo = (
    <AlertDialog open={confirmando} onOpenChange={(abierto) => !ocupado && setConfirmando(abierto)}>
      <AlertDialogContent data-testid="wa-bloquear-dialogo">
        <AlertDialogHeader>
          <AlertDialogTitle>¿Bloquear a {nombre || "este cliente"}?</AlertDialogTitle>
          <AlertDialogDescription>{AVISO_BLOQUEO}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={ocupado} data-testid="wa-bloquear-cancelar">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={ocupado}
            onClick={(e) => {
              e.preventDefault();
              cambiar(true);
            }}
            className="bg-red-600 text-white hover:bg-red-700"
            data-testid="wa-bloquear-confirmar"
          >
            Bloquear
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { bloqueado, ocupado, pedir: () => setConfirmando(true), desbloquear: () => cambiar(false), dialogo };
}

// La zona de escribir de un chat bloqueado: no se puede escribir, pero la nota para el equipo sigue.
export function ZonaBloqueado({ bloqueo, onNota }) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5" data-testid="wa-bloqueado">
      <div
        className="flex min-w-0 flex-1 items-center gap-1.5 rounded-full bg-slate-200/70 px-4 py-2.5 text-sm text-slate-500"
        aria-disabled="true"
        data-testid="wa-bloqueado-texto"
      >
        <Ban className="h-4 w-4 shrink-0 text-red-600" />
        <span className="truncate">Desbloquear para escribirle</span>
      </div>
      {onNota ? <BotonNota onClick={onNota} /> : null}
      <button
        type="button"
        onClick={bloqueo.desbloquear}
        disabled={bloqueo.ocupado}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        data-testid="wa-desbloquear"
      >
        Desbloquear
      </button>
    </div>
  );
}

// El bloque "Bloqueo" de la ficha del cliente.
export function BloqueoEnFicha({ bloqueo, sinNumero }) {
  if (bloqueo.bloqueado) {
    return (
      <div className="space-y-2" data-testid="wa-ficha-bloqueo">
        <ChipBloqueado />
        <p className="text-slate-500">No puede escribirte por este número.</p>
        <Button size="sm" variant="outline" onClick={bloqueo.desbloquear} disabled={bloqueo.ocupado} data-testid="wa-ficha-desbloquear">
          Desbloquear
        </Button>
      </div>
    );
  }
  if (sinNumero) return <span className="text-slate-500">No se puede bloquear: el cliente no tiene número visible.</span>;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={bloqueo.pedir}
      disabled={bloqueo.ocupado}
      className="gap-1.5 text-red-700 hover:text-red-800"
      data-testid="wa-ficha-bloquear"
    >
      <Ban className="h-3.5 w-3.5" />
      Bloquear cliente
    </Button>
  );
}
